import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Nav, Button, Card, Table, Form, InputGroup, FormControl, Badge, Collapse } from 'react-bootstrap';
import Navbar from '../layout/Navbar';
import axios from 'axios';
import { 
  FaHome, FaUserInjured, FaUserMd, FaPills, FaFileMedical,
  FaCalendarCheck, FaFileInvoiceDollar, FaListOl, FaChartLine,
  FaCog, FaMoneyBillWave, FaReceipt, FaSave, FaUsers, FaDatabase,
  FaChevronDown, FaChevronRight, FaSearch, FaPlus, FaArchive, FaKey, FaTrash, 
  FaEdit, FaPrint, FaUserPlus, FaArrowRight, FaCheck, FaTimes, FaExclamationTriangle, FaStepForward, FaEye, FaDownload, FaToggleOff, FaCalendarPlus
} from 'react-icons/fa';

const UserAccessControl = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [clinic, setClinic] = useState({});
  const [reportsOpen, setReportsOpen] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [activePage, setActivePage] = useState('patients');
  
  const [permissions, setPermissions] = useState({
    dashboard: false,
    patients: false,
    doctors: false,
    appointments: false,
    queue: false,
    medicines: false,
    reports: false,
    appointmentReports: false,
    finance: false,
    billing: false,
    deposits: false,
    financialReports: false,
    expenses: false,
    salaries: false,
    admin: false,
    users: false,
    register: false,
    roles: false,
    settings: false,
    backup: false,
    approveOnlineAppointment: false,
    rejectOnlineAppointment: false,
    deleteOnlineAppointment: false,
    onlineAppointments: false,
    rescheduleAppointment: false,
    addPatient: false,
    viewPatient: false,
    newReport: false,
    archivePatient: false,
    enableLogin: false,
    deletePatient: false,
    viewArchive: false,
    createBill: false,
    viewBill: false,
    editBill: false,
    payment: false,
    refund: false,
    printBill: false,
    deleteBill: false,
    addDoctor: false,
    viewDoctor: false,
    editDoctor: false,
    toggleDoctor: false,
    deleteDoctor: false,
    bookAppointment: false,
    editAppointment: false,
    deleteAppointment: false,
    payAppointment: false,
    queueAppointment: false,
    printAppointment: false,
    printTicket: false,
    callIn: false,
    skipQueue: false,
    noShow: false,
    addMedicine: false,
    viewMedicine: false,
    editMedicine: false,
    deleteMedicine: false,
    viewReportAction: false,
    editReportAction: false,
    archiveReportAction: false,
    deleteReportAction: false,
    printReceipt: false,
    recordPayment: false,
    viewDeposit: false,
    printDeposit: false,
    deleteDeposit: false,
    editTransfer: false,
    deleteTransfer: false,
    exportPDF: false,
    paySalary: false,
    printSalaryReport: false,
    editSalary: false,
    deleteSalary: false,
    addNew: false,
    addExpenseType: false,
    addExpense: false,
    createBackup: false,
    restore: false
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    
    const fetchClinic = async () => {
      try {
        const res = await axios.get('/api/clinic.php');
        setClinic(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    
    const fetchPermissions = async () => {
      if (id) {
        try {
          const res = await axios.get(`/api/user-permissions.php?userId=${id}`);
          if (res.data.success && res.data.permissions && Object.keys(res.data.permissions).length > 0) {
            setPermissions(res.data.permissions);
            
            // Auto-open sections if they have checked permissions
            const perms = res.data.permissions;
            if (perms.reports || perms.appointmentReports) {
              setReportsOpen(true);
            }
            if (perms.billing || perms.deposits || perms.financialReports || perms.expenses || perms.salaries) {
              setFinanceOpen(true);
            }
            if (perms.users || perms.register || perms.roles || perms.settings || perms.backup) {
              setAdminOpen(true);
            }
          }
        } catch (err) {
          console.error('Error fetching permissions:', err);
        }
      }
    };
    
    fetchClinic();
    fetchPermissions();
  }, [id]);

  const togglePermission = (key) => {
    setPermissions(prev => ({...prev, [key]: !prev[key]}));
  };

  const handleSave = async () => {
    try {
      await axios.put(`/api/user-permissions.php?userId=${id}`, { userId: id, permissions });
      alert('Access permissions saved successfully! User needs to logout and login again to see changes.');
    } catch (error) {
      console.error('Error saving permissions:', error);
      alert('Failed to save permissions. Please try again.');
    }
  };

  const activeColor = '#1565c0';

  return (
    <>
      <Navbar clinic={clinic} />
      <Container fluid>
        <Row>
          {/* Sidebar with checkboxes */}
          <Col md={2} className="sidebar p-0" style={{backgroundColor: '#2c3e50', minHeight: '100vh'}}>
            <Nav className="flex-column pt-3 sidebar-nav">
              <div 
                className="d-flex align-items-center py-3 px-3" 
                style={{backgroundColor: activePage === 'dashboard' ? activeColor : 'transparent', color: activePage === 'dashboard' ? '#ffffff' : '#e0e0e0', cursor: 'pointer'}}
                onClick={() => setActivePage('dashboard')}
              >
                <Form.Check 
                  type="checkbox" 
                  checked={permissions.dashboard} 
                  onChange={(e) => {e.stopPropagation(); togglePermission('dashboard');}} 
                  className="me-2"
                  style={{position: 'relative', zIndex: 1000}}
                />
                <span><FaHome className="me-2" /> Dashboard</span>
              </div>
              
              <div 
                className="d-flex align-items-center py-3 px-3" 
                style={{backgroundColor: activePage === 'patients' ? activeColor : 'transparent', color: activePage === 'patients' ? '#ffffff' : '#e0e0e0', cursor: 'pointer'}}
                onClick={() => setActivePage('patients')}
              >
                <Form.Check 
                  type="checkbox" 
                  checked={permissions.patients} 
                  onChange={(e) => {e.stopPropagation(); togglePermission('patients');}} 
                  className="me-2"
                  style={{position: 'relative', zIndex: 1000}}
                />
                <span><FaUserInjured className="me-2" /> Patients</span>
              </div>
              
              <div 
                className="d-flex align-items-center py-3 px-3" 
                style={{backgroundColor: activePage === 'doctors' ? activeColor : 'transparent', color: activePage === 'doctors' ? '#ffffff' : '#e0e0e0', cursor: 'pointer'}}
                onClick={() => setActivePage('doctors')}
              >
                <Form.Check 
                  type="checkbox" 
                  checked={permissions.doctors} 
                  onChange={(e) => {e.stopPropagation(); togglePermission('doctors');}} 
                  className="me-2"
                  style={{position: 'relative', zIndex: 1000}}
                />
                <span><FaUserMd className="me-2" /> Doctors</span>
              </div>
              
              <div 
                className="d-flex align-items-center py-3 px-3" 
                style={{backgroundColor: activePage === 'appointments' ? activeColor : 'transparent', color: activePage === 'appointments' ? '#ffffff' : '#e0e0e0', cursor: 'pointer'}}
                onClick={() => setActivePage('appointments')}
              >
                <Form.Check 
                  type="checkbox" 
                  checked={permissions.appointments} 
                  onChange={(e) => {e.stopPropagation(); togglePermission('appointments');}} 
                  className="me-2"
                  style={{position: 'relative', zIndex: 1000}}
                />
                <span><FaCalendarCheck className="me-2" /> Appointments</span>
              </div>
              
              <div 
                className="d-flex align-items-center py-3 px-3" 
                style={{backgroundColor: activePage === 'onlineAppointments' ? activeColor : 'transparent', color: activePage === 'onlineAppointments' ? '#ffffff' : '#e0e0e0', cursor: 'pointer'}}
                onClick={() => setActivePage('onlineAppointments')}
              >
                <Form.Check 
                  type="checkbox" 
                  checked={permissions.onlineAppointments} 
                  onChange={(e) => {e.stopPropagation(); togglePermission('onlineAppointments');}} 
                  className="me-2"
                  style={{position: 'relative', zIndex: 1000}}
                />
                <span><FaCalendarCheck className="me-2" /> Online Appointments</span>
              </div>
              
              <div 
                className="d-flex align-items-center py-3 px-3" 
                style={{backgroundColor: activePage === 'queue' ? activeColor : 'transparent', color: activePage === 'queue' ? '#ffffff' : '#e0e0e0', cursor: 'pointer'}}
                onClick={() => setActivePage('queue')}
              >
                <Form.Check 
                  type="checkbox" 
                  checked={permissions.queue} 
                  onChange={(e) => {e.stopPropagation(); togglePermission('queue');}} 
                  className="me-2"
                  style={{position: 'relative', zIndex: 1000}}
                />
                <span><FaListOl className="me-2" /> Patient Queue</span>
              </div>
              
              <div 
                className="d-flex align-items-center py-3 px-3" 
                style={{backgroundColor: activePage === 'medicines' ? activeColor : 'transparent', color: activePage === 'medicines' ? '#ffffff' : '#e0e0e0', cursor: 'pointer'}}
                onClick={() => setActivePage('medicines')}
              >
                <Form.Check 
                  type="checkbox" 
                  checked={permissions.medicines} 
                  onChange={(e) => {e.stopPropagation(); togglePermission('medicines');}} 
                  className="me-2"
                  style={{position: 'relative', zIndex: 1000}}
                />
                <span><FaPills className="me-2" /> Medicines</span>
              </div>
              
              <div className="border-top border-secondary my-3"></div>
              
              <div 
                onClick={() => setReportsOpen(!reportsOpen)}
                className="py-3 px-3 d-flex justify-content-between align-items-center"
                style={{color: '#e0e0e0', cursor: 'pointer'}}
              >
                <span><FaFileMedical className="me-2" /> Medical Reports & Billing</span>
                {reportsOpen ? <FaChevronDown /> : <FaChevronRight />}
              </div>
              
              <Collapse in={reportsOpen}>
                <div>
                  <div 
                    className="d-flex align-items-center py-2 ps-4 pe-3" 
                    style={{backgroundColor: activePage === 'reports' ? activeColor : 'transparent', color: activePage === 'reports' ? '#ffffff' : '#e0e0e0', cursor: 'pointer'}}
                    onClick={() => setActivePage('reports')}
                  >
                    <Form.Check 
                      type="checkbox" 
                      checked={permissions.reports} 
                      onChange={(e) => {e.stopPropagation(); togglePermission('reports');}} 
                      className="me-2"
                      style={{position: 'relative', zIndex: 1000}}
                    />
                    <span><FaFileMedical className="me-2" /> Medical Reports</span>
                  </div>
                  
                  <div 
                    className="d-flex align-items-center py-2 ps-4 pe-3" 
                    style={{backgroundColor: activePage === 'appointmentReports' ? activeColor : 'transparent', color: activePage === 'appointmentReports' ? '#ffffff' : '#e0e0e0', cursor: 'pointer'}}
                    onClick={() => setActivePage('appointmentReports')}
                  >
                    <Form.Check 
                      type="checkbox" 
                      checked={permissions.appointmentReports} 
                      onChange={(e) => {e.stopPropagation(); togglePermission('appointmentReports');}} 
                      className="me-2"
                      style={{position: 'relative', zIndex: 1000}}
                    />
                    <span><FaCalendarCheck className="me-2" /> Appointment Reports</span>
                  </div>
                </div>
              </Collapse>
              
              <div 
                onClick={() => setFinanceOpen(!financeOpen)}
                className="py-3 px-3 d-flex justify-content-between align-items-center"
                style={{color: '#e0e0e0', cursor: 'pointer'}}
              >
                <span><FaFileInvoiceDollar className="me-2" /> Finance</span>
                {financeOpen ? <FaChevronDown /> : <FaChevronRight />}
              </div>
              
              <Collapse in={financeOpen}>
                <div>
                  <div 
                    className="d-flex align-items-center py-2 ps-4 pe-3" 
                    style={{backgroundColor: activePage === 'billing' ? activeColor : 'transparent', color: activePage === 'billing' ? '#ffffff' : '#e0e0e0', cursor: 'pointer'}}
                    onClick={() => setActivePage('billing')}
                  >
                    <Form.Check 
                      type="checkbox" 
                      checked={permissions.billing} 
                      onChange={(e) => {e.stopPropagation(); togglePermission('billing');}} 
                      className="me-2"
                      style={{position: 'relative', zIndex: 1000}}
                    />
                    <span><FaFileInvoiceDollar className="me-2" /> Billing</span>
                  </div>
                  
                  <div 
                    className="d-flex align-items-center py-2 ps-4 pe-3" 
                    style={{backgroundColor: activePage === 'deposits' ? activeColor : 'transparent', color: activePage === 'deposits' ? '#ffffff' : '#e0e0e0', cursor: 'pointer'}}
                    onClick={() => setActivePage('deposits')}
                  >
                    <Form.Check 
                      type="checkbox" 
                      checked={permissions.deposits} 
                      onChange={(e) => {e.stopPropagation(); togglePermission('deposits');}} 
                      className="me-2"
                      style={{position: 'relative', zIndex: 1000}}
                    />
                    <span><FaMoneyBillWave className="me-2" /> Deposit Collection</span>
                  </div>
                  
                  <div 
                    className="d-flex align-items-center py-2 ps-4 pe-3" 
                    style={{backgroundColor: activePage === 'financialReports' ? activeColor : 'transparent', color: activePage === 'financialReports' ? '#ffffff' : '#e0e0e0', cursor: 'pointer'}}
                    onClick={() => setActivePage('financialReports')}
                  >
                    <Form.Check 
                      type="checkbox" 
                      checked={permissions.financialReports} 
                      onChange={(e) => {e.stopPropagation(); togglePermission('financialReports');}} 
                      className="me-2"
                      style={{position: 'relative', zIndex: 1000}}
                    />
                    <span><FaChartLine className="me-2" /> Financial Reports</span>
                  </div>
                  
                  <div 
                    className="d-flex align-items-center py-2 ps-4 pe-3" 
                    style={{backgroundColor: activePage === 'expenses' ? activeColor : 'transparent', color: activePage === 'expenses' ? '#ffffff' : '#e0e0e0', cursor: 'pointer'}}
                    onClick={() => setActivePage('expenses')}
                  >
                    <Form.Check 
                      type="checkbox" 
                      checked={permissions.expenses} 
                      onChange={(e) => {e.stopPropagation(); togglePermission('expenses');}} 
                      className="me-2"
                      style={{position: 'relative', zIndex: 1000}}
                    />
                    <span><FaReceipt className="me-2" /> Expenses</span>
                  </div>
                  
                  <div 
                    className="d-flex align-items-center py-2 ps-4 pe-3" 
                    style={{backgroundColor: activePage === 'salaries' ? activeColor : 'transparent', color: activePage === 'salaries' ? '#ffffff' : '#e0e0e0', cursor: 'pointer'}}
                    onClick={() => setActivePage('salaries')}
                  >
                    <Form.Check 
                      type="checkbox" 
                      checked={permissions.salaries} 
                      onChange={(e) => {e.stopPropagation(); togglePermission('salaries');}} 
                      className="me-2"
                      style={{position: 'relative', zIndex: 1000}}
                    />
                    <span><FaMoneyBillWave className="me-2" /> Doctor Salaries</span>
                  </div>
                </div>
              </Collapse>
              
              <div className="border-top border-secondary my-3"></div>
              
              <div 
                onClick={() => setAdminOpen(!adminOpen)}
                className="py-3 px-3 d-flex justify-content-between align-items-center"
                style={{color: '#e0e0e0', cursor: 'pointer'}}
              >
                <span><FaCog className="me-2" /> Administration</span>
                {adminOpen ? <FaChevronDown /> : <FaChevronRight />}
              </div>
              
              <Collapse in={adminOpen}>
                <div>
                  <div 
                    className="d-flex align-items-center py-2 ps-4 pe-3" 
                    style={{backgroundColor: activePage === 'users' ? activeColor : 'transparent', color: activePage === 'users' ? '#ffffff' : '#e0e0e0', cursor: 'pointer'}}
                    onClick={() => setActivePage('users')}
                  >
                    <Form.Check 
                      type="checkbox" 
                      checked={permissions.users} 
                      onChange={(e) => {e.stopPropagation(); togglePermission('users');}} 
                      className="me-2"
                      style={{position: 'relative', zIndex: 1000}}
                    />
                    <span><FaUsers className="me-2" /> Users</span>
                  </div>
                  
                  <div 
                    className="d-flex align-items-center py-2 ps-4 pe-3" 
                    style={{backgroundColor: activePage === 'register' ? activeColor : 'transparent', color: activePage === 'register' ? '#ffffff' : '#e0e0e0', cursor: 'pointer'}}
                    onClick={() => setActivePage('register')}
                  >
                    <Form.Check 
                      type="checkbox" 
                      checked={permissions.register} 
                      onChange={(e) => {e.stopPropagation(); togglePermission('register');}} 
                      className="me-2"
                      style={{position: 'relative', zIndex: 1000}}
                    />
                    <span><FaUsers className="me-2" /> Register User</span>
                  </div>
                  
                  <div 
                    className="d-flex align-items-center py-2 ps-4 pe-3" 
                    style={{backgroundColor: activePage === 'roles' ? activeColor : 'transparent', color: activePage === 'roles' ? '#ffffff' : '#e0e0e0', cursor: 'pointer'}}
                    onClick={() => setActivePage('roles')}
                  >
                    <Form.Check 
                      type="checkbox" 
                      checked={permissions.roles} 
                      onChange={(e) => {e.stopPropagation(); togglePermission('roles');}} 
                      className="me-2"
                      style={{position: 'relative', zIndex: 1000}}
                    />
                    <span><FaUsers className="me-2" /> Manage Roles</span>
                  </div>
                  
                  <div 
                    className="d-flex align-items-center py-2 ps-4 pe-3" 
                    style={{backgroundColor: activePage === 'settings' ? activeColor : 'transparent', color: activePage === 'settings' ? '#ffffff' : '#e0e0e0', cursor: 'pointer'}}
                    onClick={() => setActivePage('settings')}
                  >
                    <Form.Check 
                      type="checkbox" 
                      checked={permissions.settings} 
                      onChange={(e) => {e.stopPropagation(); togglePermission('settings');}} 
                      className="me-2"
                      style={{position: 'relative', zIndex: 1000}}
                    />
                    <span><FaCog className="me-2" /> Settings</span>
                  </div>
                  
                  <div 
                    className="d-flex align-items-center py-2 ps-4 pe-3" 
                    style={{backgroundColor: activePage === 'backup' ? activeColor : 'transparent', color: activePage === 'backup' ? '#ffffff' : '#e0e0e0', cursor: 'pointer'}}
                    onClick={() => setActivePage('backup')}
                  >
                    <Form.Check 
                      type="checkbox" 
                      checked={permissions.backup} 
                      onChange={(e) => {e.stopPropagation(); togglePermission('backup');}} 
                      className="me-2"
                      style={{position: 'relative', zIndex: 1000}}
                    />
                    <span><FaDatabase className="me-2" /> Backup & Restore</span>
                  </div>
                </div>
              </Collapse>
            </Nav>
          </Col>

          {/* Main Content Area */}
          <Col xs={12} md={10} className="content" style={{backgroundColor: '#f8f9fa', minHeight: '100vh', padding: '30px'}}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2>{activePage === 'dashboard' && 'Dashboard'}
                  {activePage === 'patients' && 'Patients'}
                  {activePage === 'doctors' && 'Doctors'}
                  {activePage === 'appointments' && 'Appointments'}
                  {activePage === 'onlineAppointments' && 'Online Appointments'}
                  {activePage === 'queue' && 'Patient Queue'}
                  {activePage === 'medicines' && 'Medicines'}
                  {activePage === 'reports' && 'Medical Reports'}
                  {activePage === 'appointmentReports' && 'Appointment Reports'}
                  {activePage === 'billing' && 'Billing'}
                  {activePage === 'deposits' && 'Deposit Collection'}
                  {activePage === 'financialReports' && 'Financial Reports'}
                  {activePage === 'expenses' && 'Expenses'}
                  {activePage === 'salaries' && 'Doctor Salaries'}
                  {activePage === 'users' && 'Users'}
                  {activePage === 'register' && 'Register User'}
                  {activePage === 'roles' && 'Manage Roles'}
                  {activePage === 'settings' && 'Settings'}
                  {activePage === 'backup' && 'Backup & Restore'}
              </h2>
              <div>
                <Button variant="success" onClick={handleSave} className="me-2">
                  <FaSave className="me-2" /> Save Permissions
                </Button>
                <Button variant="secondary" onClick={() => navigate('/users')}>
                  Cancel
                </Button>
              </div>
            </div>

            {/* Patients Page */}
            {activePage === 'patients' && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2>Patients</h2>
                  <div>
                    <div className="d-inline-flex align-items-center me-2">
                      <Form.Check 
                        type="checkbox" 
                        checked={permissions.viewArchive} 
                        onChange={() => togglePermission('viewArchive')} 
                        className="me-2"
                        style={{position: 'relative', zIndex: 1000}}
                      />
                      <Button variant="secondary" size="sm">
                        <FaArchive className="me-2" /> View Archive
                      </Button>
                    </div>
                    <div className="d-inline-flex align-items-center">
                      <Form.Check 
                        type="checkbox" 
                        checked={permissions.addPatient} 
                        onChange={() => togglePermission('addPatient')} 
                        className="me-2"
                        style={{position: 'relative', zIndex: 1000}}
                      />
                      <Button variant="primary">
                        <FaPlus className="me-2" /> Add New Patient
                      </Button>
                    </div>
                  </div>
                </div>

                <Card className="mb-4">
                  <Card.Header>
                    <InputGroup>
                      <FormControl placeholder="Search by name, ID or contact number" />
                      <Button variant="outline-secondary">
                        <FaSearch />
                      </Button>
                    </InputGroup>
                  </Card.Header>
                  <Card.Body>
                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th>Patient ID</th>
                          <th>Name</th>
                          <th>Age</th>
                          <th>Gender</th>
                          <th>Contact</th>
                          <th>Queue Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="patient-id">P-001</td>
                          <td>Ahmed Ali</td>
                          <td>35</td>
                          <td>Male</td>
                          <td>0300-1234567</td>
                          <td><Badge bg="success">Queue Available</Badge></td>
                          <td>
                            <div className="d-inline-flex align-items-center me-1">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.viewPatient} 
                                onChange={() => togglePermission('viewPatient')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="info" size="sm">View</Button>
                            </div>
                            <div className="d-inline-flex align-items-center me-1">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.newReport} 
                                onChange={() => togglePermission('newReport')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="success" size="sm">
                                <FaFileMedical className="me-1" /> New Report
                              </Button>
                            </div>
                            <div className="d-inline-flex align-items-center me-1">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.archivePatient} 
                                onChange={() => togglePermission('archivePatient')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="secondary" size="sm">
                                <FaArchive />
                              </Button>
                            </div>
                            <div className="d-inline-flex align-items-center me-1">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.enableLogin} 
                                onChange={() => togglePermission('enableLogin')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="warning" size="sm">
                                <FaKey />
                              </Button>
                            </div>
                            <div className="d-inline-flex align-items-center">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.deletePatient} 
                                onChange={() => togglePermission('deletePatient')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="danger" size="sm">
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </>
            )}

            {/* Appointment Reports Page */}
            {activePage === 'appointmentReports' && (
              <Card>
                <Card.Header className="bg-white py-3">
                  <h5 className="mb-0 fw-bold">Appointment Details</h5>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="border-0 py-3">Date</th>
                        <th className="border-0 py-3">Time</th>
                        <th className="border-0 py-3">Patient</th>
                        <th className="border-0 py-3">Doctor</th>
                        <th className="border-0 py-3">Type</th>
                        <th className="border-0 py-3 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="py-3">15/01/2024</td>
                        <td className="py-3">10:00 AM</td>
                        <td className="py-3 fw-semibold">Ahmed Ali</td>
                        <td className="py-3">Dr. Hassan</td>
                        <td className="py-3"><Badge bg="info">Regular</Badge></td>
                        <td className="py-3 text-center"><Badge bg="success">Confirmed</Badge></td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            {/* Medical Reports Page */}
            {activePage === 'reports' && (
              <Card>
                <Card.Body>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Report ID</th>
                        <th>Patient</th>
                        <th>Doctor</th>
                        <th>Date</th>
                        <th>Diagnosis</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="report-id">RPT-001</td>
                        <td>
                          Ahmed Ali
                          <div className="text-muted small">ID: P-001</div>
                        </td>
                        <td>Dr. Hassan</td>
                        <td>2024-01-15</td>
                        <td>Fever and headache, prescribed medication...</td>
                        <td>
                          <div className="d-inline-flex align-items-center me-1">
                            <Form.Check type="checkbox" checked={permissions.viewReportAction} onChange={() => togglePermission('viewReportAction')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                            <Button variant="info" size="sm">View</Button>
                          </div>
                          <div className="d-inline-flex align-items-center me-1">
                            <Form.Check type="checkbox" checked={permissions.editReportAction} onChange={() => togglePermission('editReportAction')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                            <Button variant="warning" size="sm">Edit</Button>
                          </div>
                          <div className="d-inline-flex align-items-center me-1">
                            <Form.Check type="checkbox" checked={permissions.archiveReportAction} onChange={() => togglePermission('archiveReportAction')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                            <Button variant="secondary" size="sm"><FaArchive /></Button>
                          </div>
                          <div className="d-inline-flex align-items-center">
                            <Form.Check type="checkbox" checked={permissions.deleteReportAction} onChange={() => togglePermission('deleteReportAction')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                            <Button variant="danger" size="sm"><FaTrash /></Button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}
            {/* Deposit Collection Page */}
            {activePage === 'deposits' && (
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Payment History</h5>
                  <div>
                    <div className="d-inline-flex align-items-center me-2">
                      <Form.Check type="checkbox" checked={permissions.printReceipt} onChange={() => togglePermission('printReceipt')} className="me-2" style={{position: 'relative', zIndex: 1000}} />
                      <Button variant="success" size="sm"><FaPrint /> Print Receipt</Button>
                    </div>
                    <div className="d-inline-flex align-items-center">
                      <Form.Check type="checkbox" checked={permissions.recordPayment} onChange={() => togglePermission('recordPayment')} className="me-2" style={{position: 'relative', zIndex: 1000}} />
                      <Button variant="primary" size="sm"><FaPlus /> Record Payment</Button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Doctor</th>
                        <th>Period</th>
                        <th>Collected</th>
                        <th>Transferred</th>
                        <th>Transfers</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>15/01/2024</td>
                        <td>Dr. Hassan</td>
                        <td>15/01/2024 - 15/01/2024</td>
                        <td className="fw-bold">Rs. 50,000</td>
                        <td className="text-primary fw-bold">Rs. 45,000</td>
                        <td><Badge bg="info">1 transfer(s)</Badge></td>
                        <td>
                          <div className="d-inline-flex align-items-center me-1">
                            <Form.Check type="checkbox" checked={permissions.viewDeposit} onChange={() => togglePermission('viewDeposit')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                            <Button variant="outline-secondary" size="sm"><FaEye /> View</Button>
                          </div>
                          <div className="d-inline-flex align-items-center me-1">
                            <Form.Check type="checkbox" checked={permissions.printDeposit} onChange={() => togglePermission('printDeposit')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                            <Button variant="outline-primary" size="sm"><FaPrint /> Print</Button>
                          </div>
                          <div className="d-inline-flex align-items-center">
                            <Form.Check type="checkbox" checked={permissions.deleteDeposit} onChange={() => togglePermission('deleteDeposit')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                            <Button variant="outline-danger" size="sm"><FaTrash /> Delete</Button>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="7" style={{backgroundColor: '#f8f9fa', padding: '20px'}}>
                          <div className="mb-3">
                            <h6 className="text-muted mb-2">Transfer Details:</h6>
                            <Table responsive size="sm" bordered>
                              <thead className="table-light">
                                <tr>
                                  <th>Time</th>
                                  <th>Collected</th>
                                  <th>Transferred</th>
                                  <th>Received By</th>
                                  <th>Notes</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>10:30 AM</td>
                                  <td>Rs. 50,000</td>
                                  <td className="fw-bold">Rs. 45,000</td>
                                  <td>Admin</td>
                                  <td>Daily collection</td>
                                  <td>
                                    <div className="d-inline-flex align-items-center me-1">
                                      <Form.Check type="checkbox" checked={permissions.editTransfer} onChange={() => togglePermission('editTransfer')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                      <Button variant="outline-warning" size="sm"><FaEdit /></Button>
                                    </div>
                                    <div className="d-inline-flex align-items-center">
                                      <Form.Check type="checkbox" checked={permissions.deleteTransfer} onChange={() => togglePermission('deleteTransfer')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                      <Button variant="outline-danger" size="sm"><FaTrash /></Button>
                                    </div>
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            {/* Billing Page */}
            {activePage === 'billing' && (
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Billing</h5>
                  <div>
                    <div className="d-inline-flex align-items-center me-2">
                      <Form.Check 
                        type="checkbox" 
                        checked={permissions.createBill} 
                        onChange={() => togglePermission('createBill')} 
                        className="me-2"
                        style={{position: 'relative', zIndex: 1000}}
                      />
                      <Button variant="success" size="sm">Create Bill</Button>
                    </div>
                    <div className="d-inline-flex align-items-center me-2">
                      <Form.Check 
                        type="checkbox" 
                        checked={permissions.editBill} 
                        onChange={() => togglePermission('editBill')} 
                        className="me-2"
                        style={{position: 'relative', zIndex: 1000}}
                      />
                      <Button variant="primary" size="sm">Edit Bill</Button>
                    </div>
                    <div className="d-inline-flex align-items-center me-2">
                      <Form.Check 
                        type="checkbox" 
                        checked={permissions.printBill} 
                        onChange={() => togglePermission('printBill')} 
                        className="me-2"
                        style={{position: 'relative', zIndex: 1000}}
                      />
                      <Button variant="secondary" size="sm">Print Bill</Button>
                    </div>
                    <div className="d-inline-flex align-items-center me-2">
                      <Form.Check 
                        type="checkbox" 
                        checked={permissions.deleteBill} 
                        onChange={() => togglePermission('deleteBill')} 
                        className="me-2"
                        style={{position: 'relative', zIndex: 1000}}
                      />
                      <Button variant="danger" size="sm">Delete Bill</Button>
                    </div>
                    <div className="d-inline-flex align-items-center">
                      <Form.Check 
                        type="checkbox" 
                        checked={permissions.refund} 
                        onChange={() => togglePermission('refund')} 
                        className="me-2"
                        style={{position: 'relative', zIndex: 1000}}
                      />
                      <Button variant="warning" size="sm">Refund</Button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Bill #</th>
                        <th>Patient</th>
                        <th>Doctor</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>BILL-001</td>
                        <td>Ahmed Ali</td>
                        <td>Dr. Hassan</td>
                        <td>Rs. 5,000</td>
                        <td><Badge bg="success">Paid</Badge></td>
                        <td>
                          <div className="d-inline-flex align-items-center me-1">
                            <Form.Check 
                              type="checkbox" 
                              checked={permissions.viewBill} 
                              onChange={() => togglePermission('viewBill')} 
                              className="me-1"
                              style={{position: 'relative', zIndex: 1000}}
                            />
                            <Button variant="info" size="sm">View</Button>
                          </div>
                          <div className="d-inline-flex align-items-center">
                            <Form.Check 
                              type="checkbox" 
                              checked={permissions.printBill} 
                              onChange={() => togglePermission('printBill')} 
                              className="me-1"
                              style={{position: 'relative', zIndex: 1000}}
                            />
                            <Button variant="primary" size="sm">Print</Button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            {/* Financial Reports Page */}
            {activePage === 'financialReports' && (
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center py-3">
                  <h5 className="mb-0 fw-bold">Transaction Details</h5>
                  <div className="d-inline-flex align-items-center">
                    <Form.Check type="checkbox" checked={permissions.exportPDF} onChange={() => togglePermission('exportPDF')} className="me-2" style={{position: 'relative', zIndex: 1000}} />
                    <Button variant="success" size="sm"><FaDownload /> Export PDF</Button>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="border-0 py-3">Date</th>
                        <th className="border-0 py-3">Bill #</th>
                        <th className="border-0 py-3">Patient</th>
                        <th className="border-0 py-3">Doctor</th>
                        <th className="border-0 py-3 text-end">Total Amount</th>
                        <th className="border-0 py-3 text-end">Paid Amount</th>
                        <th className="border-0 py-3 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="py-3">15/01/2024</td>
                        <td className="py-3"><span className="badge bg-secondary">BILL-001</span></td>
                        <td className="py-3 fw-semibold">Ahmed Ali</td>
                        <td className="py-3">Dr. Hassan</td>
                        <td className="py-3 text-end fw-bold">Rs. 5,000</td>
                        <td className="py-3 text-end text-primary fw-bold">Rs. 5,000</td>
                        <td className="py-3 text-center">
                          <Badge bg="success" className="px-3 py-2">✓ Paid</Badge>
                        </td>
                      </tr>
                    </tbody>
                    <tfoot className="bg-light">
                      <tr>
                        <td colSpan="4" className="py-3 fw-bold">TOTAL</td>
                        <td className="py-3 text-end fw-bold text-success">Rs. 5,000</td>
                        <td className="py-3 text-end fw-bold text-primary">Rs. 5,000</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </Table>
                </Card.Body>
              </Card>
            )}

            {/* Doctor Salaries Page */}
            {activePage === 'salaries' && (
              <>
                <Card className="border-0 shadow-sm mb-4">
                  <Card.Header className="bg-dark text-white py-3 d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 fw-bold">📋 Payroll Report - January 2024</h5>
                    <div>
                      <div className="d-inline-flex align-items-center me-2">
                        <Form.Check type="checkbox" checked={permissions.paySalary} onChange={() => togglePermission('paySalary')} className="me-2" style={{position: 'relative', zIndex: 1000}} />
                        <Button variant="primary" size="sm"><FaPlus /> Pay Salary</Button>
                      </div>
                      <div className="d-inline-flex align-items-center">
                        <Form.Check type="checkbox" checked={permissions.printSalaryReport} onChange={() => togglePermission('printSalaryReport')} className="me-2" style={{position: 'relative', zIndex: 1000}} />
                        <Button variant="success" size="sm"><FaPrint /> Print Report</Button>
                      </div>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table responsive className="mb-0">
                      <thead className="bg-dark text-white">
                        <tr>
                          <th className="py-3">Doctor</th>
                          <th className="py-3">Employee ID</th>
                          <th className="py-3">Pay Date</th>
                          <th className="py-3">Basic Salary</th>
                          <th className="py-3">Allowances</th>
                          <th className="py-3">Deductions</th>
                          <th className="py-3">Net Salary</th>
                          <th className="py-3">Payment Method</th>
                          <th className="py-3 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="py-3">Dr. Hassan</td>
                          <td className="py-3">DOC-001</td>
                          <td className="py-3">15/01/2024</td>
                          <td className="py-3 fw-bold">Rs. 80,000</td>
                          <td className="py-3">Rs. 10,000</td>
                          <td className="py-3">Rs. 5,000</td>
                          <td className="py-3 fw-bold text-success">Rs. 85,000</td>
                          <td className="py-3"><Badge bg="info">Bank Transfer</Badge></td>
                          <td className="py-3 text-center">
                            <div className="d-inline-flex align-items-center me-1">
                              <Form.Check type="checkbox" checked={permissions.editSalary} onChange={() => togglePermission('editSalary')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                              <Button variant="outline-primary" size="sm"><FaEdit /></Button>
                            </div>
                            <div className="d-inline-flex align-items-center">
                              <Form.Check type="checkbox" checked={permissions.deleteSalary} onChange={() => togglePermission('deleteSalary')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                              <Button variant="outline-danger" size="sm"><FaTrash /></Button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                    <div className="bg-light p-3 d-flex justify-content-between align-items-center">
                      <strong>Total Monthly Payout:</strong>
                      <Badge bg="primary" className="fs-5 px-4 py-2">Rs. 85,000</Badge>
                    </div>
                  </Card.Body>
                </Card>

                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-white py-3">
                    <h5 className="mb-0 fw-bold"><FaUsers className="me-2" />Doctor Salary Overview</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6} lg={4} className="mb-3">
                        <Card className="border shadow-sm h-100">
                          <Card.Body>
                            <h6 className="fw-bold">Dr. Hassan Ali</h6>
                            <p className="text-muted small mb-2">Cardiologist</p>
                            <div className="mb-2">
                              <strong>Base Salary:</strong> Rs. 80,000
                            </div>
                            <div className="mb-2">
                              <strong>Paid This Year:</strong> Rs. 960,000
                            </div>
                            <div className="mb-3">
                              <strong>Last Payment:</strong> 15/01/2024
                            </div>
                            <div className="d-flex gap-2">
                              <div className="d-inline-flex align-items-center">
                                <Form.Check type="checkbox" checked={permissions.paySalary} onChange={() => togglePermission('paySalary')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Badge bg="success" className="px-3 py-2"><FaCheck /> Paid</Badge>
                              </div>
                              <div className="d-inline-flex align-items-center">
                                <Form.Check type="checkbox" checked={permissions.viewDeposit} onChange={() => togglePermission('viewDeposit')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="outline-info" size="sm"><FaUserMd /> History</Button>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={6} lg={4} className="mb-3">
                        <Card className="border shadow-sm h-100">
                          <Card.Body>
                            <h6 className="fw-bold">Dr. Ayesha Khan</h6>
                            <p className="text-muted small mb-2">Pediatrician</p>
                            <div className="mb-2">
                              <strong>Base Salary:</strong> Rs. 70,000
                            </div>
                            <div className="mb-2">
                              <strong>Paid This Year:</strong> Rs. 840,000
                            </div>
                            <div className="mb-3">
                              <strong>Last Payment:</strong> 10/12/2023
                            </div>
                            <div className="d-flex gap-2">
                              <div className="d-inline-flex align-items-center">
                                <Form.Check type="checkbox" checked={permissions.paySalary} onChange={() => togglePermission('paySalary')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="outline-primary" size="sm"><FaMoneyBillWave /> Pay Now</Button>
                              </div>
                              <div className="d-inline-flex align-items-center">
                                <Form.Check type="checkbox" checked={permissions.viewDeposit} onChange={() => togglePermission('viewDeposit')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="outline-info" size="sm"><FaUserMd /> History</Button>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </>
            )}

            {/* Doctors Page */}
            {activePage === 'doctors' && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2>Doctors</h2>
                  <div>
                    <div className="d-inline-flex align-items-center">
                      <Form.Check 
                        type="checkbox" 
                        checked={permissions.addDoctor} 
                        onChange={() => togglePermission('addDoctor')} 
                        className="me-2"
                        style={{position: 'relative', zIndex: 1000}}
                      />
                      <Button variant="primary">
                        <FaPlus className="me-2" /> Add New Doctor
                      </Button>
                    </div>
                  </div>
                </div>

                <Card className="mb-4">
                  <Card.Body>
                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Specialization</th>
                          <th>Contact</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Dr. Hassan Ali</td>
                          <td>Cardiologist</td>
                          <td>0300-1111111</td>
                          <td><Badge bg="success">Active</Badge></td>
                          <td>
                            <div className="d-inline-flex align-items-center me-1">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.viewDoctor} 
                                onChange={() => togglePermission('viewDoctor')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="info" size="sm"><FaUserMd /> Portal</Button>
                            </div>
                            <div className="d-inline-flex align-items-center me-1">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.editDoctor} 
                                onChange={() => togglePermission('editDoctor')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="warning" size="sm"><FaEdit /> Edit</Button>
                            </div>
                            <div className="d-inline-flex align-items-center me-1">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.toggleDoctor} 
                                onChange={() => togglePermission('toggleDoctor')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="danger" size="sm"><FaToggleOff /> Toggle</Button>
                            </div>
                            <div className="d-inline-flex align-items-center">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.deleteDoctor} 
                                onChange={() => togglePermission('deleteDoctor')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="danger" size="sm"><FaTrash /> Delete</Button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </>
            )}

            {/* Appointments Page */}
            {activePage === 'appointments' && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2>Appointments</h2>
                  <div>
                    <div className="d-inline-flex align-items-center">
                      <Form.Check 
                        type="checkbox" 
                        checked={permissions.bookAppointment} 
                        onChange={() => togglePermission('bookAppointment')} 
                        className="me-2"
                        style={{position: 'relative', zIndex: 1000}}
                      />
                      <Button variant="primary">
                        <FaCalendarPlus className="me-2" /> New Appointment
                      </Button>
                    </div>
                  </div>
                </div>

                <Card className="mb-4">
                  <Card.Body>
                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Patient</th>
                          <th>Doctor</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>10:00 AM</td>
                          <td>Ahmed Ali</td>
                          <td>Dr. Hassan</td>
                          <td><Badge bg="success">Confirmed</Badge></td>
                          <td>
                            <div className="d-inline-flex align-items-center me-1">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.editBill} 
                                onChange={() => togglePermission('editBill')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="outline-primary" size="sm"><FaEdit /> Edit</Button>
                            </div>
                            <div className="d-inline-flex align-items-center me-1">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.rescheduleAppointment} 
                                onChange={() => togglePermission('rescheduleAppointment')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="outline-warning" size="sm"><FaCalendarPlus /> Reschedule</Button>
                            </div>
                            <div className="d-inline-flex align-items-center me-1">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.deletePatient} 
                                onChange={() => togglePermission('deletePatient')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="outline-danger" size="sm"><FaTrash /> Delete</Button>
                            </div>
                            <div className="d-inline-flex align-items-center me-1">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.payment} 
                                onChange={() => togglePermission('payment')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="info" size="sm"><FaFileInvoiceDollar /> Pay</Button>
                            </div>
                            <div className="d-inline-flex align-items-center me-1">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.addPatient} 
                                onChange={() => togglePermission('addPatient')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="outline-success" size="sm"><FaUserPlus /> Queue</Button>
                            </div>
                            <div className="d-inline-flex align-items-center">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.printBill} 
                                onChange={() => togglePermission('printBill')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="outline-info" size="sm"><FaPrint /> Print</Button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </>
            )}

            {/* Online Appointments Page */}
            {activePage === 'onlineAppointments' && (
              <Card>
                <Card.Body>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Patient Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Doctor</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>15/01/2024</td>
                        <td>Ahmed Ali</td>
                        <td>ahmed@email.com</td>
                        <td>0300-1234567</td>
                        <td>Dr. Hassan</td>
                        <td><Badge bg="warning">Pending</Badge></td>
                        <td>
                          <div className="d-inline-flex align-items-center me-1">
                            <Form.Check 
                              type="checkbox" 
                              checked={permissions.approveOnlineAppointment} 
                              onChange={() => togglePermission('approveOnlineAppointment')} 
                              className="me-1"
                              style={{position: 'relative', zIndex: 1000}}
                            />
                            <Button variant="success" size="sm">Approve</Button>
                          </div>
                          <div className="d-inline-flex align-items-center me-1">
                            <Form.Check 
                              type="checkbox" 
                              checked={permissions.rejectOnlineAppointment} 
                              onChange={() => togglePermission('rejectOnlineAppointment')} 
                              className="me-1"
                              style={{position: 'relative', zIndex: 1000}}
                            />
                            <Button variant="danger" size="sm">Reject</Button>
                          </div>
                          <div className="d-inline-flex align-items-center">
                            <Form.Check 
                              type="checkbox" 
                              checked={permissions.deleteOnlineAppointment} 
                              onChange={() => togglePermission('deleteOnlineAppointment')} 
                              className="me-1"
                              style={{position: 'relative', zIndex: 1000}}
                            />
                            <Button variant="outline-danger" size="sm"><FaTrash /></Button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            {/* Patient Queue Page */}
            {activePage === 'queue' && (
              <>
                <Card className="mb-4">
                  <Card.Body>
                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Patient</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>1</td>
                          <td>Ahmed Ali</td>
                          <td><Badge bg="warning">Waiting</Badge></td>
                          <td>
                            <div className="d-inline-flex align-items-center me-1">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.printTicket} 
                                onChange={() => togglePermission('printTicket')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="info" size="sm"><FaPrint /> Ticket</Button>
                            </div>
                            <div className="d-inline-flex align-items-center me-1">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.callIn} 
                                onChange={() => togglePermission('callIn')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="primary" size="sm"><FaArrowRight /> Call In</Button>
                            </div>
                            <div className="d-inline-flex align-items-center me-1">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.skipQueue} 
                                onChange={() => togglePermission('skipQueue')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="warning" size="sm"><FaStepForward /> Skip</Button>
                            </div>
                            <div className="d-inline-flex align-items-center">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.noShow} 
                                onChange={() => togglePermission('noShow')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="danger" size="sm"><FaTimes /> No Show</Button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </>
            )}

            {/* Medicines Page */}
            {activePage === 'medicines' && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2>Medicines</h2>
                  <div>
                    <div className="d-inline-flex align-items-center">
                      <Form.Check 
                        type="checkbox" 
                        checked={permissions.addMedicine} 
                        onChange={() => togglePermission('addMedicine')} 
                        className="me-2"
                        style={{position: 'relative', zIndex: 1000}}
                      />
                      <Button variant="primary">
                        <FaPlus className="me-2" /> Add New Medicine
                      </Button>
                    </div>
                  </div>
                </div>

                <Card className="mb-4">
                  <Card.Body>
                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th>Medicine Name</th>
                          <th>Category</th>
                          <th>Stock</th>
                          <th>Price</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Panadol</td>
                          <td>Pain Relief</td>
                          <td>500</td>
                          <td>Rs. 50</td>
                          <td>
                            <div className="d-inline-flex align-items-center me-1">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.viewMedicine} 
                                onChange={() => togglePermission('viewMedicine')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="info" size="sm">View</Button>
                            </div>
                            <div className="d-inline-flex align-items-center me-1">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.editMedicine} 
                                onChange={() => togglePermission('editMedicine')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="warning" size="sm">Edit</Button>
                            </div>
                            <div className="d-inline-flex align-items-center">
                              <Form.Check 
                                type="checkbox" 
                                checked={permissions.deleteMedicine} 
                                onChange={() => togglePermission('deleteMedicine')} 
                                className="me-1"
                                style={{position: 'relative', zIndex: 1000}}
                              />
                              <Button variant="danger" size="sm">Delete</Button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </>
            )}

            {/* Other Pages */}
            {!['patients', 'billing', 'reports', 'appointmentReports', 'deposits', 'financialReports', 'salaries', 'doctors', 'appointments', 'queue', 'medicines'].includes(activePage) && (
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">{activePage === 'dashboard' && 'Dashboard Overview'}
                      {activePage === 'doctors' && 'Doctors List'}
                      {activePage === 'appointments' && 'Appointments'}
                      {activePage === 'queue' && 'Patient Queue'}
                      {activePage === 'medicines' && 'Medicines Inventory'}
                      {activePage === 'reports' && 'Medical Reports'}
                      {activePage === 'appointmentReports' && 'Appointment Reports'}
                      {activePage === 'deposits' && 'Deposit Collection'}
                      {activePage === 'financialReports' && 'Financial Reports'}
                      {activePage === 'expenses' && 'Expenses'}
                      {activePage === 'salaries' && 'Doctor Salaries'}
                      {activePage === 'users' && 'Users Management'}
                      {activePage === 'register' && 'Register New User'}
                      {activePage === 'roles' && 'Manage Roles'}
                      {activePage === 'settings' && 'System Settings'}
                      {activePage === 'backup' && 'Backup & Restore'}
                  </h5>
                  <div>
                    {(activePage === 'doctors' || activePage === 'medicines' || activePage === 'users') && (
                      <div className="d-inline-flex align-items-center">
                        <Form.Check type="checkbox" checked={permissions.addNew} onChange={() => togglePermission('addNew')} className="me-2" style={{position: 'relative', zIndex: 1000}} />
                        <Button variant="primary" size="sm">
                          <FaPlus className="me-2" /> Add New
                        </Button>
                      </div>
                    )}
                    {activePage === 'appointments' && (
                      <div className="d-inline-flex align-items-center">
                        <Form.Check type="checkbox" checked={permissions.bookAppointment} onChange={() => togglePermission('bookAppointment')} className="me-2" style={{position: 'relative', zIndex: 1000}} />
                        <Button variant="primary" size="sm">
                          <FaPlus className="me-2" /> Book Appointment
                        </Button>
                      </div>
                    )}
                    {activePage === 'expenses' && (
                      <div className="d-inline-flex align-items-center me-2">
                        <Form.Check type="checkbox" checked={permissions.addExpenseType} onChange={() => togglePermission('addExpenseType')} className="me-2" style={{position: 'relative', zIndex: 1000}} />
                        <Button variant="outline-primary" size="sm">
                          <FaPlus className="me-2" /> Add Expense Type
                        </Button>
                      </div>
                    )}
                    {activePage === 'expenses' && (
                      <div className="d-inline-flex align-items-center">
                        <Form.Check type="checkbox" checked={permissions.addExpense} onChange={() => togglePermission('addExpense')} className="me-2" style={{position: 'relative', zIndex: 1000}} />
                        <Button variant="primary" size="sm">
                          <FaPlus className="me-2" /> Add Expense
                        </Button>
                      </div>
                    )}
                    {activePage === 'backup' && (
                      <>
                        <div className="d-inline-flex align-items-center me-2">
                          <Form.Check type="checkbox" checked={permissions.createBackup} onChange={() => togglePermission('createBackup')} className="me-2" style={{position: 'relative', zIndex: 1000}} />
                          <Button variant="success" size="sm">Create Backup</Button>
                        </div>
                        <div className="d-inline-flex align-items-center">
                          <Form.Check type="checkbox" checked={permissions.restore} onChange={() => togglePermission('restore')} className="me-2" style={{position: 'relative', zIndex: 1000}} />
                          <Button variant="warning" size="sm">Restore</Button>
                        </div>
                      </>
                    )}
                  </div>
                </Card.Header>
                <Card.Body>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        {activePage === 'doctors' && (
                          <>
                            <th>Doctor ID</th>
                            <th>Name</th>
                            <th>Specialization</th>
                            <th>Contact</th>
                            <th>Actions</th>
                          </>
                        )}
                        {activePage === 'appointments' && (
                          <>
                            <th>Time</th>
                            <th>Patient</th>
                            <th>Doctor</th>
                            <th>Room</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>Queue Status</th>
                            <th>Actions</th>
                          </>
                        )}
                        {activePage === 'queue' && (
                          <>
                            <th width="5%">#</th>
                            <th width="30%">Patient</th>
                            <th width="20%">Appointment</th>
                            <th width="10%">Status</th>
                            <th width="10%">Wait Time</th>
                            <th width="25%">Actions</th>
                          </>
                        )}
                        {activePage === 'medicines' && (
                          <>
                            <th>Medicine Name</th>
                            <th>Category</th>
                            <th>Stock</th>
                            <th>Price</th>
                            <th>Actions</th>
                          </>
                        )}
                        {activePage === 'expenses' && (
                          <>
                            <th>Date</th>
                            <th>Type</th>
                            <th>Description</th>
                            <th>Amount</th>
                            <th>Actions</th>
                          </>
                        )}
                        {activePage === 'users' && (
                          <>
                            <th>Username</th>
                            <th>Name</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </>
                        )}
                        {(activePage === 'reports' || activePage === 'appointmentReports' || activePage === 'financialReports' || activePage === 'salaries' || activePage === 'deposits') && (
                          <>
                            <th>Report ID</th>
                            <th>Patient</th>
                            <th>Doctor</th>
                            <th>Date</th>
                            <th>Diagnosis</th>
                            <th>Actions</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {activePage === 'doctors' && (
                          <>
                            <td>DOC-001</td>
                            <td>Dr. Hassan Ali</td>
                            <td>Cardiologist</td>
                            <td>0300-1111111</td>
                            <td>
                              <div className="d-inline-flex align-items-center me-1">
                                <Form.Check type="checkbox" checked={permissions.viewPatient} onChange={() => togglePermission('viewPatient')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="info" size="sm"><FaUserMd /></Button>
                              </div>
                              <div className="d-inline-flex align-items-center me-1">
                                <Form.Check type="checkbox" checked={permissions.editBill} onChange={() => togglePermission('editBill')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="warning" size="sm"><FaEdit /></Button>
                              </div>
                              <div className="d-inline-flex align-items-center me-1">
                                <Form.Check type="checkbox" checked={permissions.deletePatient} onChange={() => togglePermission('deletePatient')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="danger" size="sm"><FaToggleOff /></Button>
                              </div>
                              <div className="d-inline-flex align-items-center">
                                <Form.Check type="checkbox" checked={permissions.deletePatient} onChange={() => togglePermission('deletePatient')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="danger" size="sm"><FaTrash /></Button>
                              </div>
                            </td>
                          </>
                        )}
                        {activePage === 'appointments' && (
                          <>
                            <td>10:00 AM</td>
                            <td>
                              Ahmed Ali
                              <div className="small text-muted">ID: P-001</div>
                            </td>
                            <td>Dr. Hassan</td>
                            <td>Room 1</td>
                            <td><Badge bg="secondary">Regular</Badge></td>
                            <td><Badge bg="success">Confirmed</Badge></td>
                            <td><Badge bg="success">Queue Available</Badge></td>
                            <td>
                              <div className="d-flex gap-1">
                                <div className="btn-group me-1">
                                  <div className="d-inline-flex align-items-center">
                                    <Form.Check type="checkbox" checked={permissions.editBill} onChange={() => togglePermission('editBill')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                    <Button variant="outline-primary" size="sm"><FaEdit /></Button>
                                  </div>
                                  <div className="d-inline-flex align-items-center">
                                    <Form.Check type="checkbox" checked={permissions.deletePatient} onChange={() => togglePermission('deletePatient')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                    <Button variant="outline-danger" size="sm"><FaTrash /></Button>
                                  </div>
                                </div>
                                <div className="d-inline-flex align-items-center me-1">
                                  <Form.Check type="checkbox" checked={permissions.payment} onChange={() => togglePermission('payment')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                  <Button variant="info" size="sm"><FaFileInvoiceDollar /> Pay</Button>
                                </div>
                                <div className="d-inline-flex align-items-center me-1">
                                  <Form.Check type="checkbox" checked={permissions.addPatient} onChange={() => togglePermission('addPatient')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                  <Button variant="outline-success" size="sm"><FaUserPlus /> Queue</Button>
                                </div>
                                <div className="d-inline-flex align-items-center">
                                  <Form.Check type="checkbox" checked={permissions.printBill} onChange={() => togglePermission('printBill')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                  <Button variant="outline-info" size="sm"><FaPrint /></Button>
                                </div>
                              </div>
                            </td>
                          </>
                        )}
                        {activePage === 'queue' && (
                          <>
                            <td>1 <FaExclamationTriangle className="ms-2 text-danger" title="Emergency" /></td>
                            <td>
                              <div>Ahmed Ali</div>
                              <small className="text-muted">35 yrs, Male</small>
                            </td>
                            <td>
                              <div>10:00 AM</div>
                              <Badge bg="info">Regular</Badge>
                            </td>
                            <td><Badge bg="warning">Waiting</Badge></td>
                            <td className="text-danger fw-bold">45m</td>
                            <td>
                              <div className="d-inline-flex align-items-center me-1">
                                <Form.Check type="checkbox" checked={permissions.printBill} onChange={() => togglePermission('printBill')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="info" size="sm"><FaPrint /> Ticket</Button>
                              </div>
                              <div className="d-inline-flex align-items-center me-1">
                                <Form.Check type="checkbox" checked={permissions.viewPatient} onChange={() => togglePermission('viewPatient')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="primary" size="sm"><FaArrowRight /> Call In</Button>
                              </div>
                              <div className="d-inline-flex align-items-center me-1">
                                <Form.Check type="checkbox" checked={permissions.editBill} onChange={() => togglePermission('editBill')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="warning" size="sm"><FaStepForward /> Skip</Button>
                              </div>
                              <div className="d-inline-flex align-items-center">
                                <Form.Check type="checkbox" checked={permissions.deletePatient} onChange={() => togglePermission('deletePatient')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="danger" size="sm"><FaTimes /> No Show</Button>
                              </div>
                            </td>
                          </>
                        )}
                        {activePage === 'medicines' && (
                          <>
                            <td>Panadol</td>
                            <td>Pain Relief</td>
                            <td>500</td>
                            <td>Rs. 50</td>
                            <td>
                              <div className="d-inline-flex align-items-center me-1">
                                <Form.Check type="checkbox" checked={permissions.viewPatient} onChange={() => togglePermission('viewPatient')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="info" size="sm">View</Button>
                              </div>
                              <div className="d-inline-flex align-items-center me-1">
                                <Form.Check type="checkbox" checked={permissions.editBill} onChange={() => togglePermission('editBill')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="warning" size="sm">Edit</Button>
                              </div>
                              <div className="d-inline-flex align-items-center">
                                <Form.Check type="checkbox" checked={permissions.deletePatient} onChange={() => togglePermission('deletePatient')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="danger" size="sm">Delete</Button>
                              </div>
                            </td>
                          </>
                        )}
                        {activePage === 'expenses' && (
                          <>
                            <td>2024-01-15</td>
                            <td><Badge bg="info">Utilities</Badge></td>
                            <td>Electricity Bill</td>
                            <td>Rs. 15,000</td>
                            <td>
                              <div className="d-inline-flex align-items-center me-1">
                                <Form.Check type="checkbox" checked={permissions.editBill} onChange={() => togglePermission('editBill')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="outline-primary" size="sm"><FaEdit /></Button>
                              </div>
                              <div className="d-inline-flex align-items-center">
                                <Form.Check type="checkbox" checked={permissions.deletePatient} onChange={() => togglePermission('deletePatient')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="outline-danger" size="sm"><FaTrash /></Button>
                              </div>
                            </td>
                          </>
                        )}
                        {activePage === 'users' && (
                          <>
                            <td>admin</td>
                            <td>Admin User</td>
                            <td><Badge bg="danger">Admin</Badge></td>
                            <td><Badge bg="success">Active</Badge></td>
                            <td>
                              <div className="d-inline-flex align-items-center me-1">
                                <Form.Check type="checkbox" checked={permissions.viewPatient} onChange={() => togglePermission('viewPatient')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="primary" size="sm">View</Button>
                              </div>
                              <div className="d-inline-flex align-items-center me-1">
                                <Form.Check type="checkbox" checked={permissions.editBill} onChange={() => togglePermission('editBill')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="warning" size="sm">Reset Password</Button>
                              </div>
                              <div className="d-inline-flex align-items-center me-1">
                                <Form.Check type="checkbox" checked={permissions.editBill} onChange={() => togglePermission('editBill')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="info" size="sm">Edit</Button>
                              </div>
                              <div className="d-inline-flex align-items-center me-1">
                                <Form.Check type="checkbox" checked={permissions.viewPatient} onChange={() => togglePermission('viewPatient')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="outline-info" size="sm">Access</Button>
                              </div>
                              <div className="d-inline-flex align-items-center me-1">
                                <Form.Check type="checkbox" checked={permissions.deletePatient} onChange={() => togglePermission('deletePatient')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="outline-danger" size="sm">Deactivate</Button>
                              </div>
                              <div className="d-inline-flex align-items-center">
                                <Form.Check type="checkbox" checked={permissions.deletePatient} onChange={() => togglePermission('deletePatient')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="danger" size="sm">Delete</Button>
                              </div>
                            </td>
                          </>
                        )}
                        {(activePage === 'reports' || activePage === 'appointmentReports' || activePage === 'financialReports' || activePage === 'salaries' || activePage === 'deposits') && (
                          <>
                            <td className="report-id">RPT-001</td>
                            <td>
                              Ahmed Ali
                              <div className="text-muted small">ID: P-001</div>
                            </td>
                            <td>Dr. Hassan</td>
                            <td>2024-01-15</td>
                            <td>Fever and headache, prescribed medication...</td>
                            <td>
                              <div className="d-inline-flex align-items-center me-1">
                                <Form.Check type="checkbox" checked={permissions.viewReportAction} onChange={() => togglePermission('viewReportAction')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="info" size="sm">View</Button>
                              </div>
                              <div className="d-inline-flex align-items-center me-1">
                                <Form.Check type="checkbox" checked={permissions.editReportAction} onChange={() => togglePermission('editReportAction')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="warning" size="sm">Edit</Button>
                              </div>
                              <div className="d-inline-flex align-items-center me-1">
                                <Form.Check type="checkbox" checked={permissions.archiveReportAction} onChange={() => togglePermission('archiveReportAction')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="secondary" size="sm"><FaArchive /></Button>
                              </div>
                              <div className="d-inline-flex align-items-center">
                                <Form.Check type="checkbox" checked={permissions.deleteReportAction} onChange={() => togglePermission('deleteReportAction')} className="me-1" style={{position: 'relative', zIndex: 1000}} />
                                <Button variant="danger" size="sm"><FaTrash /></Button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default UserAccessControl;
